import { defineStore } from 'pinia';
import { ref } from 'vue';

import {
  usePaxStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';
import type { CloseHotelSelectionFiltersOrigin } from '@apps/main/new-desktop/core/models';
import { MapStateKey } from '@apps/main/modules/state/state';
import { injectStrict } from '@apps/main/modules/helpers/injectStrict';

import {
  trackPaxStartChanging,
  trackPaxEndChanging,
} from '@libs/google-analytics-events';
import type {
  AutoCompleteSuggestion,
  IngredientId,
  Pax,
} from '@libs/trip-planner-interfaces';

import { HotelSelectionModalType } from '../models/hotelSelectionModals';

import { useHotelSelectionDestinationSelectorStore } from './destinationSelector/destinationSelectorStore';
import { useHotelSelectionStore } from './hotelSelection/hotelSelectionStore';

export const useHotelSelectionModalsStore = defineStore(
  'HotelSelectionModalsStore',
  () => {
    const mapState = injectStrict(MapStateKey);
    const paxStore = usePaxStore();
    const hotelSelectionStore = useHotelSelectionStore();
    const destinationSelectorStore =
      useHotelSelectionDestinationSelectorStore();
    const statsigEventsStore = useStatsigEventsStore();

    const { mapShouldCenterOnMarkers } = mapState;

    // State
    const hotelSelectionModalType = ref<HotelSelectionModalType>(
      HotelSelectionModalType.None
    );

    // Actions
    function closeHotelSelectionModal(
      origin?: CloseHotelSelectionFiltersOrigin
    ): void {
      hotelSelectionModalType.value = HotelSelectionModalType.None;
      if (origin) {
        hotelSelectionStore.handleCloseFiltersEvent(origin);
      }
    }

    function openPaxSelectorModal(): void {
      trackPaxStartChanging();
      statsigEventsStore.logCorePaxModalOpened({
        source: CoreEventSource.HotelSelection,
      });
      hotelSelectionModalType.value = HotelSelectionModalType.PaxSelector;
    }

    function confirmPaxSelectorModal(newPax: Pax): void {
      trackPaxEndChanging();
      paxStore.updatePax(newPax, CoreEventSource.HotelSelection);
      hotelSelectionModalType.value = HotelSelectionModalType.None;
    }

    function openDestinationSelectorModal(
      searchText: string,
      ingredientId: IngredientId | undefined
    ): void {
      destinationSelectorStore.setDestinationSelectorData(
        searchText,
        ingredientId
      );
      hotelSelectionModalType.value =
        HotelSelectionModalType.DestinationSelector;
    }

    function confirmDestinationSelectorModal(
      newDestination: AutoCompleteSuggestion | null | undefined
    ): void {
      if (newDestination) {
        mapShouldCenterOnMarkers.value = true;
        destinationSelectorStore.handleConfirmSelection(newDestination);
      }
      closeHotelSelectionModal();
    }

    function openFiltersModal(): void {
      hotelSelectionModalType.value =
        HotelSelectionModalType.MobileFiltersModal;
    }

    function openWhyWeSuggestModal(): void {
      hotelSelectionModalType.value = HotelSelectionModalType.WhyWeSuggest;
    }

    return {
      // State
      hotelSelectionModalType,

      // Actions
      closeHotelSelectionModal,
      openPaxSelectorModal,
      confirmPaxSelectorModal,
      openDestinationSelectorModal,
      confirmDestinationSelectorModal,
      openFiltersModal,
      openWhyWeSuggestModal,
    };
  }
);
